import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../app/RootReducer";
import { UserAddresses } from "../../Entity/User";
import { updateUserAddressRep } from "../../Repository/UserRepository";
import t from "../../../components/I18N/TranslateHelpers";
import { GetCurrentLocale, SelectInterface } from "../../../components/Utils";
import { SpinnerOnTop } from "../../../components/SpinnerOnTop";
import { allCountries } from "../../../components/Countries";
import { renderSelect } from "../../../components/Forms/FormsComponent";
import {addUserAddress, deleteUserAddress, getUserAccount, getUserAddress} from "../UsersSlice";

const AddressesPage = () => {
  const dispatch = useDispatch();
  const userAddresses = useSelector((state: RootState) => state.userRdc.userAddresses ?? []);
  const isRequestingAddNewAddress = useSelector((state: RootState) => state.userRdc.isRequestingAddNewAddress);
  const isRequestingGetAddresses = useSelector((state: RootState) => state.userRdc.isRequestingGetAddresses);
  const userAccount = useSelector((state: RootState) => state.userRdc.userDetail?.customer);

  const locale = GetCurrentLocale();
  const countries = useMemo(() => allCountries(locale), [locale]);

  const [resolvedDefaultAddressId, setResolvedDefaultAddressId] = useState<number | null>(null);
  const [localAddresses, setLocalAddresses] = useState<UserAddresses[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<Record<number, SelectInterface | null>>({});
  const [newAddress, setNewAddress] = useState<Partial<UserAddresses>>({});
  const [selectedCountryAdd, setSelectedCountryAdd] = useState<SelectInterface | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof UserAddresses, boolean>>>({});

  const prevRef = useRef<{ addresses: UserAddresses[]; defaultId: number | null }>({
    addresses: [],
    defaultId: null,
  });

  useEffect(() => {
    dispatch(getUserAddress());
    dispatch(getUserAccount());
  }, [dispatch]);

  useEffect(() => {
    if (userAccount?.defaultAddress) {
      const id = parseInt(userAccount.defaultAddress.split("/").pop()!, 10);
      setResolvedDefaultAddressId(id);
    }
  }, [userAccount]);

  useEffect(() => {
    const previous = prevRef.current;
    const hasAddressesChanged =
      previous.addresses.length !== userAddresses.length ||
      previous.defaultId !== resolvedDefaultAddressId ||
      previous.addresses.some((addr, i) => addr.id !== userAddresses[i]?.id);

    if (!hasAddressesChanged) return;

    const sorted = [...userAddresses].sort((a, b) => {
      if (a.id === resolvedDefaultAddressId) return -1;
      if (b.id === resolvedDefaultAddressId) return 1;
      return 0;
    });

    setLocalAddresses(sorted.map((a) => ({ ...a })));

    const updated: Record<number, SelectInterface | null> = {};
    sorted.forEach((addr) => {
      if (addr.id) {
        updated[addr.id] = countries.find((c) => c.value === addr.countryCode) ?? null;
      }
    });
    setSelectedCountries(updated);

    prevRef.current = {
      addresses: userAddresses,
      defaultId: resolvedDefaultAddressId,
    };
  }, [userAddresses, countries, resolvedDefaultAddressId]);

  const handleFieldChange = (addressId: number, field: keyof UserAddresses, value: string) => {
    setLocalAddresses((prev) =>
      prev.map((addr) =>
        addr.id === addressId ? { ...addr, [field]: value } : addr
      )
    );
  };

  const handleUpdate = async (address: UserAddresses) => {
    const { id, street, postcode, city, countryCode } = address;
    if (!id) return;
    await updateUserAddressRep(id, { street, postcode, city, countryCode });
    dispatch(getUserAddress());
  };

  const handleDelete = async (id: number | null) => {
    if (!id) return;
    setLocalAddresses((prev) => prev.filter(addr => addr.id !== id));
    await dispatch(deleteUserAddress(id));
  };

  const handleAdd = async () => {
    const { street, postcode, city, countryCode } = newAddress;

    const newErrors: typeof errors = {};
    if (!street) newErrors.street = true;
    if (!postcode) newErrors.postcode = true;
    if (!city) newErrors.city = true;
    if (!countryCode) newErrors.countryCode = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    await dispatch(addUserAddress(newAddress as UserAddresses));
    await dispatch(getUserAccount());
    await dispatch(getUserAddress());
    setNewAddress({});
    setSelectedCountryAdd(null);
  };

  return (
    <>
      {isRequestingGetAddresses ? (
        <SpinnerOnTop />
      ) : (
        <div className="row">
          {localAddresses.map((address) => (
            <div key={address.id} className="col-md-6">
              <div className="box bg-light row border rounded p-3 mb-4 g-0">
                {Number(address.id) === Number(resolvedDefaultAddressId) && (
                  <h4 className="mb-3">{t('main_address')}</h4>
                )}
                {["street", "postcode", "city"].map((field) => (
                  <div className="col-md-6 col-sm-12 mb-3 gx-3" key={field}>
                    <label className="required-field">{t(field)}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={address[field as keyof UserAddresses] || ""}
                      onChange={(e) =>
                        handleFieldChange(address.id!, field as keyof UserAddresses, e.target.value)
                      }
                    />
                  </div>
                ))}
                <div className="col-md-6 col-sm-12 mb-3 gx-3">
                  {renderSelect(
                    locale,
                    t("country"),
                    `countryCode_${address.id}`,
                    (selected: SelectInterface | null) => {
                      setSelectedCountries((prev) => ({
                        ...prev,
                        [address.id!]: selected,
                      }));
                      handleFieldChange(address.id!, "countryCode", selected?.value || "");
                    },
                    true,
                    countries,
                    selectedCountries[address.id!] ?? countries.find(c => c.value === address.countryCode) ?? null
                  )}
                </div>
                <div className="col-md-12">
                  <button className="btn btn-primary" onClick={() => handleUpdate(address)}>
                    {t("save")}
                  </button>
                  {Number(address.id) !== Number(resolvedDefaultAddressId) && (
                    <button
                      className="btn btn-danger text-danger ml-2"
                      onClick={() => handleDelete(address.id)}
                    >
                      {t("delete")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {isRequestingAddNewAddress ? (
        <SpinnerOnTop/>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); handleAdd(); }} className="col-md-6 border p-3">
          <h3 className="mb-5">{t("add_new_address")}</h3>
          <div className="row">
            {["street", "postcode", "city"].map((field) => (
              <div className="col-md-6 col-sm-12 mb-3" key={field}>
                <label className="required-field">{t(field)}</label>
                <input
                  type="text"
                  className={`form-control ${errors[field as keyof UserAddresses] ? "is-invalid" : ""}`}
                  value={newAddress[field as keyof UserAddresses] || ""}
                  onChange={(e) =>
                    setNewAddress((prev) => ({ ...prev, [field]: e.target.value }))
                  }
                />
                {errors[field as keyof UserAddresses] && (
                  <span className="invalid-feedback d-block">{t("fields_required")}</span>
                )}
              </div>
            ))}
            <div className="col-md-6 col-sm-12 mb-3">
              {renderSelect(
                locale,
                t("country"),
                "new_country",
                (selected: SelectInterface | null) => {
                  setSelectedCountryAdd(selected);
                  setNewAddress((prev) => ({
                    ...prev,
                    countryCode: selected ? selected.value : "",
                  }));
                  if (selected) {
                    setErrors((prev) => {
                      const updated = { ...prev };
                      delete updated.countryCode;
                      return updated;
                    });
                  }
                },
                true,
                countries,
                selectedCountryAdd
              )}
              {errors.countryCode && (
                <span className="invalid-feedback d-block">{t("fields_required")}</span>
              )}
            </div>
            <div className="col-md-6 col-sm-12 mb-3">
              <button className="btn btn-primary" type="submit">
                <i className="fa-solid fa-plus"></i> {t("add_address")}
              </button>
            </div>
          </div>
        </form>
      )}
    </>
  );
};

export default AddressesPage;
